import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useTheme } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings, LogOut, Building2, Moon, Sun, BarChart3, Repeat, History } from "lucide-react";
import storage from "@/utils/storage";

// Import logos
import logoMcom from "@/assets/logo192.png";
import seatLogo from "@/assets/logos/seat.png";
import cupraLogo from "@/assets/logos/cupra.png";
import kiaLogo from "@/assets/logos/kia.png";
import skodaLogo from "@/assets/logos/skoda.png";
import suzukiLogo from "@/assets/logos/suzuki.png";
import mgLogo from "@/assets/logos/mg.png";
import omodaJaecooLogo from "@/assets/logos/omoda-jaecoo.png";
import cheryLogo from "@/assets/logos/chery.png";
import voLogo from "@/assets/logos/logo-vo.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_COLORS = {
  SEAT: { color: "#E31E24", bg: "rgba(227, 30, 36, 0.1)" },      // Rouge
  CUPRA: { color: "#CD7F32", bg: "rgba(205, 127, 50, 0.1)" },    // Bronze
  SKODA: { color: "#4BA82E", bg: "rgba(75, 168, 46, 0.1)" },     // Vert
  KIA: { color: "#212E38", bg: "rgba(33, 46, 56, 0.1)" },        // Bleu foncé
  SUZUKI: { color: "#164194", bg: "rgba(22, 65, 148, 0.1)" },    // Bleu
  MG: { color: "#212E38", bg: "rgba(33, 46, 56, 0.1)" },         // Bleu foncé
  OMODA_JAECOO: { color: "#1A1A1A", bg: "rgba(26, 26, 26, 0.1)" }, // Noir
  CHERY: { color: "#1A1A1A", bg: "rgba(26, 26, 26, 0.1)" },        // Noir Chery
};

// Brand display names
const BRAND_DISPLAY_NAMES = {
  SEAT: "SEAT",
  CUPRA: "CUPRA",
  SKODA: "ŠKODA",
  KIA: "KIA",
  SUZUKI: "SUZUKI",
  MG: "MG",
  OMODA_JAECOO: "OMODA\n& JAECOO",
  CHERY: "CHERY",
};

// Brand logos mapping
const BRAND_LOGOS = {
  SEAT: seatLogo,
  CUPRA: cupraLogo,
  KIA: kiaLogo,
  SKODA: skodaLogo,
  SUZUKI: suzukiLogo,
  MG: mgLogo,
  OMODA_JAECOO: omodaJaecooLogo,
  CHERY: cheryLogo,
};

export default function HomePage() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [brands, setBrands] = useState([]);
  const [hoveredBrand, setHoveredBrand] = useState(null);
  const [session, setSession] = useState(null);

  useEffect(() => {
    // Get session from storage
    const parsedSession = storage.getJSON("emcom_session");
    if (parsedSession) {
      setSession(parsedSession);
      // Use only the brands from the concession
      setBrands(parsedSession.brands || []);
    } else {
      // Fallback to all brands if no session
      fetchBrands();
    }
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data.brands || []);
    } catch (error) {
      console.error("Error fetching brands:", error);
      setBrands(["SEAT", "CUPRA", "SKODA", "KIA", "SUZUKI", "MG"]);
    }
  };

  const handleBrandClick = (brand) => {
    navigate(`/calculator/${brand}`);
  };

  const handleLogout = () => {
    storage.removeItem("emcom_session");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="verbaere-header text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-white logo-container flex items-center justify-center shadow-md">
              <img src={logoMcom} alt="Verbaere" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-heading font-black tracking-tight">e-MCom</h1>
              <p className="text-xs text-gray-400">by Verbaere Performance Suite</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Concession info */}
            {session && (
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/10 rounded-md">
                <Building2 className="w-4 h-4 text-[#E63946]" />
                <span className="text-sm">{session.concession_name}</span>
              </div>
            )}
            {/* Dark Mode Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/10" 
              onClick={toggleTheme}
              title={isDark ? "Mode clair" : "Mode sombre"}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            {/* History Link */}
            <Link 
              to="/history" 
              className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-white/10 transition-colors"
              data-testid="history-link"
            >
              <History className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">Historique</span>
            </Link>
            <Link 
              to="/admin" 
              className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-white/10 transition-colors"
              data-testid="admin-link"
            >
              {session?.concession_id && session.concession_id !== "admin" ? (
                <>
                  <BarChart3 className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Dashboard</span>
                </>
              ) : (
                <>
                  <Settings className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Admin</span>
                </>
              )}
            </Link>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="ml-2 hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Concession Banner */}
        {session && (
          <div className="mb-8 p-4 bg-gradient-to-r from-[#212E38] to-[#1a252e] rounded-xl text-white">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-[#E63946]" />
              <div>
                <h2 className="font-heading font-bold">{session.concession_name}</h2>
                <p className="text-sm text-gray-400">{session.city}</p>
              </div>
            </div>
          </div>
        )}

        {/* Title */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-foreground mb-3">
            Sélectionnez une marque
          </h2>
          <p className="text-muted-foreground">
            {session 
              ? `Marques distribuées par ${session.concession_name}`
              : "Choisissez la marque du véhicule pour calculer la marge et la commission"
            }
          </p>
        </div>

        {/* Brand Grid - includes VO at the end */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="brand-grid">
          {brands.map((brand) => {
            const brandStyle = BRAND_COLORS[brand] || { color: "#212E38", bg: "rgba(33,46,56,0.1)" };
            const isHovered = hoveredBrand === brand;
            
            return (
              <div
                key={brand}
                className="cursor-pointer perspective-1000"
                onClick={() => handleBrandClick(brand)}
                onMouseEnter={() => setHoveredBrand(brand)}
                onMouseLeave={() => setHoveredBrand(null)}
                data-testid={`brand-card-${brand.toLowerCase()}`}
              >
                <div 
                  className="relative w-full h-[140px] transition-transform duration-500 transform-style-3d"
                  style={{ transform: isHovered ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
                >
                  {/* Front - Logo */}
                  <div className="absolute inset-0 backface-hidden flex items-center justify-center">
                    <div 
                      className="w-24 h-24 rounded-xl bg-[#212E38] flex items-center justify-center shadow-lg overflow-hidden transition-all duration-300"
                    >
                      {BRAND_LOGOS[brand] ? (
                        <img 
                          src={BRAND_LOGOS[brand]} 
                          alt={`Logo ${brand}`}
                          className="w-16 h-16 object-contain"
                          style={{ 
                            filter: 'brightness(0) invert(1)'
                          }}
                        />
                      ) : (
                        <span className="text-white font-heading font-black text-xl">{brand}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Back - Brand Name */}
                  <div 
                    className="absolute inset-0 backface-hidden flex items-center justify-center rotate-y-180"
                  >
                    <div 
                      className="w-24 h-24 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300"
                      style={{ 
                        backgroundColor: brandStyle.color,
                      }}
                    >
                      <span className="text-white font-heading font-black text-lg tracking-wide text-center whitespace-pre-line">
                        {BRAND_DISPLAY_NAMES[brand] || brand}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* VO Card - integrated in the grid with flip effect */}
          <div
            className="cursor-pointer perspective-1000"
            onClick={() => navigate("/calculator/vo")}
            onMouseEnter={() => setHoveredBrand("VO")}
            onMouseLeave={() => setHoveredBrand(null)}
            data-testid="vo-card"
          >
            <div 
              className="relative w-full h-[140px] transition-transform duration-500 transform-style-3d"
              style={{ transform: hoveredBrand === "VO" ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
            >
              {/* Front - VO Text */}
              <div className="absolute inset-0 backface-hidden flex items-center justify-center">
                <div 
                  className="w-24 h-24 rounded-xl bg-[#212E38] flex items-center justify-center shadow-lg"
                >
                  <span className="text-white font-heading font-black text-3xl">VO</span>
                </div>
              </div>
              
              {/* Back - VO Name */}
              <div className="absolute inset-0 backface-hidden flex items-center justify-center rotate-y-180">
                <div 
                  className="w-24 h-24 rounded-xl bg-[#E31E24] flex flex-col items-center justify-center shadow-lg"
                >
                  <span className="text-white font-heading font-black text-lg">VO</span>
                  <span className="text-white/80 text-[10px] mt-1">Occasion</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="verbaere-header text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} e-MCom by Verbaere Performance Suite
          </p>
        </div>
      </footer>
    </div>
  );
}
